import { useTranslation } from 'react-i18next';
import { FilterX } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { FilterBar, type FilterField } from '@/components/ui/filter-bar';
import { useBusinesses } from '@/features/business/use-businesses';
import { STATUS_FILTERS, TIME_FILTERS, TYPE_FILTERS } from './order-enums';
import { EMPTY_FILTERS, type OrderFilters } from './orders.api';

/**
 * The five order filters.
 *
 * Radix's Select cannot hold an empty-string value, so "any" is carried as the
 * sentinel `ANY` and translated back at the boundary — the API's own encoding
 * of "no filter" is an empty tag that contributes nothing to the filter string.
 *
 * Layout, and the phone behaviour where these collapse behind a Filters button,
 * come from the shared FilterBar.
 */
const ANY = 'ANY';

const toValue = (tag: string) => (tag === '' ? ANY : tag);
const fromValue = (value: string) => (value === ANY ? '' : value);

export function OrderFilterBar({
  filters,
  onChange,
  search,
  busy,
}: {
  filters: OrderFilters;
  onChange: (next: OrderFilters) => void;
  /** The page's own search form, kept beside the Filters button on a phone. */
  search?: React.ReactNode;
  /** True while the search these filters drive is in flight. */
  busy?: boolean;
}) {
  const { t } = useTranslation();
  const { data: businesses } = useBusinesses();

  const set = (patch: Partial<OrderFilters>) =>
    onChange({ ...filters, ...patch });

  const isDefault =
    filters.status === EMPTY_FILTERS.status &&
    !filters.time &&
    !filters.type &&
    !filters.payment &&
    !filters.businessId;

  const options = (
    list: readonly { tag: string; labelKey: string }[]
  ) => list.map((o) => ({ value: toValue(o.tag), label: t(o.labelKey) }));

  const fields: FilterField[] = [
    {
      id: 'status',
      label: t('orders.filters.status'),
      value: toValue(filters.status),
      onChange: (value) => set({ status: fromValue(value) }),
      // The default is ACTIVE, not "any" — that is what counts as un-narrowed.
      resetValue: EMPTY_FILTERS.status || ANY,
      sort: false,
      options: options(STATUS_FILTERS),
    },
    {
      id: 'time',
      label: t('orders.filters.time'),
      value: toValue(filters.time),
      onChange: (value) => set({ time: fromValue(value) }),
      // Past / Yesterday / Today / Tomorrow / Future is a timeline.
      sort: false,
      options: options(TIME_FILTERS),
    },
    {
      id: 'type',
      label: t('orders.filters.type'),
      value: toValue(filters.type),
      onChange: (value) => set({ type: fromValue(value) }),
      options: options(TYPE_FILTERS),
    },
    {
      id: 'payment',
      label: t('orders.filters.payment'),
      value: toValue(filters.payment),
      onChange: (value) => set({ payment: fromValue(value) }),
      options: [
        { value: ANY, label: t('orders.filters.any') },
        { value: 'CREDITCARD', label: t('orders.filters.creditCard') },
      ],
    },
    {
      id: 'store',
      label: t('orders.filters.store'),
      value: toValue(filters.businessId),
      onChange: (value) => set({ businessId: fromValue(value) }),
      options: [
        { value: ANY, label: t('orders.filters.allStores') },
        ...(businesses ?? []).map((business) => ({
          value: business.id,
          label: business.name,
        })),
      ],
    },
  ];

  return (
    <FilterBar
      busy={busy}
      search={search}
      fields={fields}
      summary={
        isDefault ? null : (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onChange(EMPTY_FILTERS)}
            className="text-muted-foreground h-7 text-xs"
          >
            <FilterX className="size-3.5" />
            {t('orders.filters.reset')}
          </Button>
        )
      }
    />
  );
}
